import { haversineDistance } from "./haversineDistance";

const globalDistanceCache = new Map<string, { duration: number; distance: number }>();
const globalPromiseCache = new Map<string, Promise<{ duration: number; distance: number }>>();

interface CacheProvider {
  distanceMode?: "haversine" | "mapbox";
  getCachedDistanceForCoordinates: (
    from: [number, number], 
    to: [number, number], 
    profile?: "driving" | "walking" | "cycling"
  ) => Promise<{ duration: number; distance: number } | null>;
}

let globalCacheProvider: CacheProvider | null = null;

export function setGlobalCacheProvider(provider: CacheProvider | null) {
  globalCacheProvider = provider;
}

export async function calculateDistance(
  from: [number, number],
  to: [number, number],
  profile: "driving" | "walking" | "cycling" = "driving",
): Promise<{ duration: number; distance: number }> {
  if (globalCacheProvider) {
    try {
      const result = await globalCacheProvider.getCachedDistanceForCoordinates(from, to, profile);
      if (result) {
        return result;
      }
    } catch (error) {
      console.warn("Failed to get distance from cache provider, falling back to direct calculation", error);
    }

    // If provider is in haversine mode, never fall through to Mapbox
    if (globalCacheProvider.distanceMode === "haversine") {
      return { distance: haversineDistance(from, to), duration: 0 };
    }
  }

  const fromRounded = [Math.round(from[0] * 1000) / 1000, Math.round(from[1] * 1000) / 1000];
  const toRounded = [Math.round(to[0] * 1000) / 1000, Math.round(to[1] * 1000) / 1000];
  const cacheKey = `${fromRounded[0]},${fromRounded[1]}-${toRounded[0]},${toRounded[1]}-${profile}`;
  
  const cached = globalDistanceCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const existingPromise = globalPromiseCache.get(cacheKey);
  if (existingPromise) {
    return existingPromise;
  }

  const promise = (async () => {
    const coordinates = `${from[1]},${from[0]};${to[1]},${to[0]}`;
    const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;

    const url = `https://api.mapbox.com/directions-matrix/v1/mapbox/${profile}/${coordinates}`;

    const params = new URLSearchParams({
      access_token: accessToken,
      annotations: "duration,distance",
    });

    const response = await fetch(`${url}?${params}`);
    const data = await response.json();

    if (data.code !== "Ok") {
      throw new Error(`Mapbox API Error: ${data.code}`);
    }

    const result = {
      duration: data.durations[0][1],
      distance: data.distances[0][1],
    };

    globalDistanceCache.set(cacheKey, result);
    globalPromiseCache.delete(cacheKey);

    return result;
  })();

  globalPromiseCache.set(cacheKey, promise);

  return promise;
}
